import Parseable from '@/misc/Parseable';

/**
 * A single payment record that was made to a promoter.
 */
export interface PromoterPayment {
    amount: number,
    period: string,
    paidAt?: string
}

/**
 * A promoter payout row computed by the backend from the configured
 * promoter rates. Kept defensive: the `promoter` field may arrive as a
 * populated object or already as a plain display name.
 */
export default class PromoterPayout extends Parseable {
    public promoter: any;
    public referredDrivers!: number;
    public referredCustomers!: number;
    /** Everything ever earned, and everything ever paid, over all time. */
    public totalEarned!: number;
    public totalPaidAmount!: number;
    /**
     * Lifetime balance: earned minus paid. Can legitimately be negative while a
     * reassignment reversal is being absorbed by future earnings.
     */
    public amountDue!: number;
    /** Breakdown for the selected period only. */
    public signupBonusTotal!: number;
    public commissionTotal!: number;
    public reversalTotal!: number;
    public commissionRides!: number;
    public payments!: PromoterPayment[];

    public static parseFromObject(object: Partial<PromoterPayout>): PromoterPayout {
        const payout = new PromoterPayout();
        Object.assign(payout, object);
        payout.referredDrivers = payout.referredDrivers ?? 0;
        payout.referredCustomers = payout.referredCustomers ?? 0;
        payout.totalEarned = payout.totalEarned ?? 0;
        payout.totalPaidAmount = payout.totalPaidAmount ?? 0;
        payout.amountDue = payout.amountDue ?? 0;
        payout.signupBonusTotal = payout.signupBonusTotal ?? 0;
        payout.commissionTotal = payout.commissionTotal ?? 0;
        payout.reversalTotal = payout.reversalTotal ?? 0;
        payout.commissionRides = payout.commissionRides ?? 0;
        payout.payments = payout.payments ?? [];
        return payout;
    }

    /**
     * The promoter id, needed for the mark-paid action.
     */
    public get promoterId(): string {
        if (this.promoter && typeof this.promoter === 'object') {
            return this.promoter.id ?? this.promoter._id ?? '';
        }
        return typeof this.promoter === 'string' ? this.promoter : '';
    }

    /**
     * A human readable promoter name for tables and the PDF.
     */
    public get promoterName(): string {
        const p = this.promoter;
        if (p && typeof p === 'object') {
            if (p.companyName) return p.companyName;
            const name = `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim();
            return name || p.name || p.mail || '-';
        }
        return typeof p === 'string' ? p : '-';
    }

    /**
     * Whether at least one payment has been recorded for this promoter.
     */
    public get isPaid(): boolean {
        return this.payments.length > 0;
    }

    public get totalPaid(): number {
        return this.payments.reduce((sum, payment) => sum + (payment.amount ?? 0), 0);
    }

    public parseToObject(): Partial<PromoterPayout> {
        return { ...this };
    }
}
